import { Controller } from "@hotwired/stimulus"

// Owns the live-preview panel lifecycle (slice 1):
//  - sample selector change reloads the preview turbo frame at the new src
//  - a manual "Run" button forces an immediate reload
//  - the status chip transitions running -> ran-at on turbo:frame-load, and is
//    marked stale when an upstream edit signals the shown result is outdated
//
// The deeper debounce/stale-on-edit-of-the-draft wiring lands when editing
// arrives; for the read-only slice the frame auto-loads (src=) and this
// controller drives re-runs + the chip. Mirrors filters_controller's posture
// of nudging a frame/form rather than fetching by hand.
export default class extends Controller {
  static targets = ["frame", "status"]
  static values = {
    statusRunning: String,
    statusStale: String,
    debounce: { type: Number, default: 600 },
  }

  initialize() {
    this.debounceTimer = null
    this.onEdited = this.onEdited.bind(this)
  }

  connect() {
    window.addEventListener("file-layout:edited", this.onEdited)
  }

  disconnect() {
    clearTimeout(this.debounceTimer)
    window.removeEventListener("file-layout:edited", this.onEdited)
  }

  // An autosaved edit landed: show the shown result as stale immediately, then
  // debounce a re-run so a burst of edits collapses into one preview run.
  onEdited() {
    this.markStale()
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.run(), this.debounceValue)
  }

  // Reload the frame at a new sample. The <select> carries the per-option
  // preview src in data-preview-src so we don't rebuild query strings here.
  selectSample(event) {
    const src = event.target.selectedOptions[0]?.dataset.previewSrc
    if (src) this.reloadAt(src)
  }

  run() {
    if (this.hasFrameTarget) this.reloadAt(this.frameTarget.src)
  }

  reloadAt(src) {
    if (!this.hasFrameTarget || !src) return

    this.markRunning()
    this.frameTarget.classList.add("opacity-50", "pointer-events-none")
    this.frameTarget.setAttribute("src", src)
    // An explicit re-run must fetch now, even when the preview pane is scrolled
    // out of view. A `loading="lazy"` frame gates every src change behind an
    // IntersectionObserver, so an off-screen re-run would never fire (on a
    // stacked/narrow layout the pane can sit below the fold indefinitely).
    // Promoting to eager stops that observer and loads the current src at once;
    // reload() then forces the fetch for an unchanged src (the Run button) too.
    // Turbo aborts any in-flight request for this frame, so the latest selection
    // always wins — a slower earlier render can't land late.
    this.frameTarget.setAttribute("loading", "eager")
    this.frameTarget.reload()
  }

  // Wired via data-action="turbo:frame-load->preview-refresh#loaded" on the
  // frame; the freshly-rendered chip already carries its ran-at label, so we
  // just clear any transient running/stale styling on the wrapper.
  loaded() {
    this.element.removeAttribute("data-preview-state")
    if (this.hasFrameTarget) {
      this.frameTarget.classList.remove("opacity-50", "pointer-events-none")
    }
  }

  markRunning() {
    this.element.setAttribute("data-preview-state", "running")
    if (this.hasStatusTarget && this.hasStatusRunningValue) {
      this.statusTarget.textContent = this.statusRunningValue
    }
  }

  // Called when an upstream edit (future slice) lands: the shown result is for
  // an older draft. Keep displaying it, labelled stale, until a re-run swaps in.
  markStale() {
    this.element.setAttribute("data-preview-state", "stale")
    if (this.hasStatusTarget && this.hasStatusStaleValue) {
      this.statusTarget.textContent = this.statusStaleValue
    }
  }
}
